/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Camera, PerspectiveCamera, Vector3, OrthographicCamera } from 'three';
import { MoveActor } from '../actors/MoveActor';
/**
 * handles all cameras of a stage
 */
export class Cameras {
    /**
     * setup
     */
    constructor() {
        this._cameras = new Map();
        this._cameraReference = new Map(); // last place of the camera
        this._tolerance = new Array(16);
        this.setTolerance(Number.EPSILON * 3);
    }
    /**
     * returns the camera description of the camera
     * @param cam
     */
    static getDescFromCamera(cam) {
        const to = new Vector3();
        cam.getWorldDirection(to);
        to.add(cam.position);
        const camDesc = { name: cam.name,
            from: cam.position.toArray(),
            up: cam.up.toArray(),
            to: to.toArray(),
            pivot: cam.userData.pivot && cam.userData.pivot.toArray() || [0, 0, 0],
            fov: 'fov' in cam ? cam.fov : 0,
            near: cam.near,
            far: cam.far,
        };
        return camDesc;
    }
    /**
     * moves to or sets a new camera position
     * @param stage the stage
     * @param targetR the target camera description
     * @param durationSec the duration of the camera movement in seconds
     */
    doTransition(stage, targetR, durationSec = 0) {
        const isCam = (targetR instanceof Camera);
        const cam = isCam ? targetR : this.get(targetR.name);
        const targetDesc = isCam ? this.getDesc(cam.name) : targetR;
        if (durationSec > 0) {
            const targetObj = new Camera();
            this._setCameraSettings(targetObj, targetDesc);
            Object.assign(cam.userData, targetObj.userData); // don't forget custom userData properties
            const transition = new MoveActor(stage, cam, 'camMove');
            transition.setDuration(durationSec);
            transition.addTarget('position', 'Vector3', cam.position, targetObj.position);
            transition.addTarget('quaternion', 'Quaternion', cam.quaternion, targetObj.quaternion);
            transition.addTarget('up', 'Vector3', cam.up, targetObj.up);
            if (cam instanceof PerspectiveCamera) {
                transition.addTarget('fov', 'number', cam.fov, targetObj.fov);
            }
            stage.addActor(transition);
        }
        else {
            this._setCameraSettings(cam, targetDesc);
        }
    }
    /**
     * returns the camera with the given name or a default camera
     * @param name
     */
    get(name) {
        if (this._cameras.has(name)) {
            return this._cameras.get(name);
        }
        else if (this._cameras.has('default')) {
            return this._cameras.get('default');
        }
        else if (this._cameras.size > 0) {
            return this._cameras.values().next().value; // the first is the 'default'
        }
        else { // nothing found => create a default camera
            return this._makeDefault();
        }
    }
    /**
     * returns the camera description of the camera with the given name
     * @param name
     */
    getDesc(name) {
        const cam = this.get(name);
        return Cameras.getDescFromCamera(cam);
    }
    /**
     * sets a camera
     * @param name
     * @param camera
     */
    set(name, camera) {
        this._cameras.set(name, camera);
    }
    /**
     * creates n cameras from a array of camera descriptions
     * @param width   screen / image width to calculate the camera aspect
     * @param height  screen / image height to calculate the camera aspect
     * @param descs
     */
    createFromDescriptions(width, height, ...descs) {
        for (const e of descs) {
            const camera = this._createCamera(e, width, height);
            this._setCameraSettings(camera, e);
            this.set(e.name, camera);
        }
    }
    /**
     * recalculates the aspect of a camera
     * @param camera
     * @param width
     * @param height
     */
    adjustCamera(camera, width, height) {
        // tslint:disable-next-line:no-any
        if (camera.isPerspectiveCamera) {
            this._setCameraAspect(camera, width, height);
        }
        else {
            // TODO : OrthoCam?
        }
    }
    hasChanged(cam) {
        const ref = this._cameraReference.get(cam.name);
        const res = (ref === undefined) || !this._camEquals(cam, ref);
        if (res) {
            this._copyAsReference(cam);
        }
        return res;
    }
    setTolerance(tolerance) {
        for (let i = 0; i < this._tolerance.length; i++) {
            this._tolerance[i] = tolerance;
        }
    }
    _camEquals(cam1, cam2) {
        const te = cam1.matrixWorld.elements;
        const me = cam2.matrixWorld.elements;
        for (let i = 0; i < 16; i++) {
            if (Math.abs(te[i] - me[i]) > this._tolerance[i]) {
                return false;
            }
        }
        return true;
    }
    _copyAsReference(cam) {
        const ref = this._cameraReference.get(cam.name);
        if (!!ref) {
            ref.copy(cam);
        }
        else {
            this._cameraReference.set(cam.name, cam.clone());
        }
    }
    _setCameraSettings(ref, e) {
        if ('fov' in e) { // TODO : make this more camera agnostic
            const pcam = ref;
            if (!!e.near) {
                pcam.near = e.near;
            }
            if (!!e.far) {
                pcam.far = e.far;
            }
        }
        if ('fov' in e) {
            ref.fov = e.fov;
        }
        ref.position.fromArray(e.from || ref.position.toArray());
        ref.up.fromArray(e.up || ref.up.toArray());
        ref.lookAt(new Vector3().fromArray(e.to || ref.getWorldDirection(new Vector3()).toArray()));
        // TODO: do we need pivot for the future in camera? or should we store this in navigator?
        if (e.pivot) {
            if (ref.userData.pivot) {
                ref.userData.pivot.fromArray(e.pivot);
            }
            else {
                ref.userData.pivot = new Vector3(...e.pivot);
            }
        }
        else {
            if (!!e.to) {
                ref.userData.pivot = new Vector3(...e.to);
            }
            else {
                ref.userData.pivot = new Vector3(0, 0, 0);
            }
        }
    }
    _createCamera(e, w, h) {
        let camera;
        if (e.fov != null && e.fov === 0) {
            const d = 2 * (e.scale || 1);
            camera = new OrthographicCamera(-w / d, w / d, h / d, -h / d, e.near || 0.1, e.far || 20000);
        }
        else {
            camera = new PerspectiveCamera(e.fov || 40, w / h, e.near || 0.1, e.far || 20000);
        }
        camera.name = e.name;
        return camera;
    }
    _setCameraAspect(camera, width, height) {
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
    }
    _makeDefault() {
        const camera = new PerspectiveCamera(30);
        camera.position.fromArray([0, 0, 80]);
        camera.lookAt(new Vector3());
        camera.name = 'default';
        this.set(camera.name, camera);
        return camera;
    }
}
//# sourceMappingURL=Cameras.js.map